import { theme } from '@Guidelines/Theme/theme';
import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import { css } from '@emotion/react';
import PeriodFilter from '@UI/Pages/Home/HomeHeader/HomeFilters/PeriodFilter';
import ScopeFilter from '@UI/Pages/Home/HomeHeader/HomeFilters/ScopeFilter';
import buildHomeScopeKey from '@UI/Pages/Home/HomeHeader/HomeFilters/Helpers/buildHomeScopeKey';
import buildScopeOptions from '@UI/Pages/Home/HomeHeader/HomeFilters/Helpers/buildScopeOptions';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import homePeriodSignal from '@UI/Pages/Home/Signals/homePeriodSignal';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { SlidersHorizontal } from 'lucide-react';

const rowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	flex: none;
`;

const toggleStyle = css`
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.625rem;
	width: 100%;
	padding: 0.75rem 1rem;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.surface};
	color: ${theme.colors.ink};
	border: 1.5px solid ${theme.colors.borderStrong};
	font-family: ${theme.fonts.body};
	font-size: 0.8125rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.2;
	cursor: pointer;

	&:hover {
		border-color: ${theme.colors.accent};
	}

	&:focus-visible {
		outline: 3px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

const summaryStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	min-width: 0;
`;

const summaryTextStyle = css`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const actionStyle = css`
	flex: none;
	color: ${theme.colors.accent};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.semibold};
`;

const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
	padding: 1.125rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};

	&[hidden] {
		display: none;
	}
`;

/**
 * Périmètre and Période filters: side by side on a wide viewport, behind a summary button that
 * unfolds them in place on a narrow one. The fields are mounted once, whichever layout is active.
 */
const HomeFilters: React.FC = () => {
	const panelId = React.useId();
	const isCompact = useIsCompactViewport();
	const [expanded, setExpanded] = React.useState(false);
	const period = useSignalValue(homePeriodSignal);
	const { organizations } = useCurrentOrganization();
	const { scope } = useHomeIndicatorScope();

	React.useEffect(() => {
		if (!isCompact) {
			setExpanded(false);
		}
	}, [isCompact]);

	const handleToggle = React.useCallback(() => {
		setExpanded((previous) => !previous);
	}, []);

	const scopeLabel = React.useMemo(() => {
		const options = buildScopeOptions(organizations);

		if (options.onlyFacilityName !== null) {
			return `Agence ${options.onlyFacilityName}`;
		}

		const key = buildHomeScopeKey(scope);
		const selected = options.groups.flatMap((group) => group.items).find((option) => option.key === key);

		return selected?.label ?? 'Tout mon périmètre';
	}, [organizations, scope]);

	if (!isCompact) {
		return (
			<div id={panelId} css={rowStyle}>
				<ScopeFilter showLabel={false} />
				<PeriodFilter showLabel={false} />
			</div>
		);
	}

	return (
		<React.Fragment>
			<button
				type="button"
				css={toggleStyle}
				aria-expanded={expanded}
				aria-controls={panelId}
				onClick={handleToggle}
			>
				<span css={summaryStyle}>
					<SlidersHorizontal size={16} aria-hidden="true" focusable="false" />
					<span css={summaryTextStyle}>{`${scopeLabel} · ${period} j`}</span>
				</span>
				<span css={actionStyle}>{expanded ? 'Fermer' : 'Filtres'}</span>
			</button>
			<div id={panelId} css={panelStyle} hidden={!expanded}>
				<ScopeFilter showLabel />
				<PeriodFilter showLabel />
			</div>
		</React.Fragment>
	);
};

export default HomeFilters;
